import { IconPrefix } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatRelative, parseJSON } from "date-fns";
import { Anchor, Box, Grid, ResponsiveContext, Tab, Tabs, Text, Image } from "grommet";
import * as React from "react";
import Avatar from "react-avatar";
import { useNavigate, useParams } from "react-router-dom";
import { AliasViewModel, getAlias } from "../aliases/AliasStore";
import { BadgeCard } from "../badges/BadgeCard";
import CustomButton from "../components/CustomButton";
import LinkAnchor from "../components/LinkAnchor";
import RichTabTitle from "../components/RichTabTitle";
import SkeletonWrapper from "../components/SkeletonWrapper";
import { LeagueRow } from "../leagues/LeagueRow";
import { getAliasLeagues, LeagueViewModel } from "../leagues/LeaguesStore";
import { getAliasMatches, MatchViewModel } from "../matches/MatchesStore";
import { MatchRow } from "../matches/MatchRow";
import { SeasonRow } from "../seasons/SeasonRow";
import { TeamGrid, TeamMenu, TeamRow } from "../teams/TeamsManagement";
import { getAliasTeams, TeamViewModel } from "../teams/TeamsStore";
import { TournamentRow } from "../tournaments/TournamentRow";
import { TournamentViewModel, getAliasTournaments } from "../tournaments/TournamentsStore";
import AliasHeader from "./AliasHeader";
import { LiveIndicator } from "./LiveIndicator";
import { useUser } from "../account/AccountStore";

const UXAliasHome = ({}) => {
    let { id } = useParams<{ id: string }>();
    const [loading, setLoading] = React.useState(true);

    const [index, setIndex] = React.useState(0);
    const onActive = (nextIndex: number) => setIndex(nextIndex);

    const [alias, setAlias] = React.useState({} as AliasViewModel);
    const [upcomingMatches, setUpcomingMatches] = React.useState([] as MatchViewModel[]);
    const [pastMatches, setPastMatches] = React.useState([] as MatchViewModel[]);
    const [teams, setTeams] = React.useState([] as TeamViewModel[]);
    const [tournaments, setTournaments] = React.useState([] as TournamentViewModel[]);
    const [leagues, setLeagues] = React.useState([] as LeagueViewModel[]);

    React.useEffect(() => {
        getAlias(id).then((alias) => {
            setAlias(alias);
            setLoading(false);
        });
        getAliasMatches(id).then((matches) => {
            setUpcomingMatches(matches.filter((m) => m.isUpcoming));
            setPastMatches(matches.filter((m) => !m.isUpcoming));

            console.log("interactive");
        });
        getAliasTeams(id).then((teams) => setTeams(teams));
        getAliasTournaments(id).then((tournaments) => setTournaments(tournaments));
        getAliasLeagues(id).then((leagues) => setLeagues(leagues));
    }, []);

    const size = React.useContext(ResponsiveContext);
    const isSmall = size === "small";
    const user = useUser();
    const navigate = useNavigate();
    return (
        <Box align="center">
            <SkeletonWrapper loading={loading}>
                <Box width={{ max: "xxlarge", min: size }} fill>
                    <AliasHeader {...alias}></AliasHeader>

                    <Box direction="row" gap={isSmall ? "xsmall" : "small"} justify="center" pad={{ top: "small", horizontal: isSmall ? "small" : "none" }} wrap>
                        <CustomButton icon={<FontAwesomeIcon icon={["fad", "fire"]} size="2x" />} label={isSmall ? "" : "Overview"} onClick={() => onActive(0)}></CustomButton>
                        <CustomButton icon={<FontAwesomeIcon icon={["fad", "shield"]} size="2x" />} label={isSmall ? "" : "Badges"} onClick={() => onActive(1)}></CustomButton>
                        <CustomButton icon={<FontAwesomeIcon icon={["fad", "gamepad-modern"]} size="2x" />} label={isSmall ? "" : "Matches"} onClick={() => onActive(2)}></CustomButton>
                        <CustomButton icon={<FontAwesomeIcon icon={["fad", "trophy"]} size="2x" />} label={isSmall ? "" : "Tournaments"} onClick={() => onActive(3)}></CustomButton>
                        <CustomButton icon={<FontAwesomeIcon icon={["fad", "table-cells"]} size="2x" />} label={isSmall ? "" : "Leagues"} onClick={() => onActive(4)}></CustomButton>
                        {user.id == alias.identityUserID && (
                            <CustomButton icon={<Image src="/images/discord-logo-blue.png" width={"110px"} />} label="" onClick={() => navigate("/discord")}></CustomButton>
                        )}
                    </Box>

                    <Tabs activeIndex={index} onActive={onActive}>
                        <Tab>
                            <Box pad="small">
                                {teams.length > 0 && <TeamGrid teams={teams}></TeamGrid>}
                                {teams.length == 0 && (
                                    <Box align="center">
                                        <Text textAlign="center">{alias.name} has no teams yet</Text>
                                    </Box>
                                )}
                            </Box>
                        </Tab>
                        <Tab>
                            <Box pad="small">
                                <Box direction="row" gap="small" wrap justify="center">
                                    {alias.badges?.length > 0 &&
                                        alias.badges?.map((c) => (
                                            <BadgeCard key={c.id} badge={c.badge} onClick={() => {}}>
                                                <Text size="small">Awarded {formatRelative(parseJSON(c.awardedOn), new Date())}</Text>
                                            </BadgeCard>
                                        ))}
                                </Box>
                                {alias.badges?.length == 0 && (
                                    <Box align="center">
                                        <Text textAlign="center">{alias.name} hasn't been awarded any badges yet</Text>
                                    </Box>
                                )}
                            </Box>
                        </Tab>
                        <Tab>
                            <Box gap="small" pad={"small"}>
                                <Text weight={"bold"} style={{ textTransform: "uppercase" }}>
                                    Upcoming Matches
                                </Text>
                                {upcomingMatches.map((match) => (
                                    <MatchRow key={match.id} match={match}></MatchRow>
                                ))}
                                {upcomingMatches.length == 0 && (
                                    <Box align="center">
                                        <Text textAlign="center">{alias.name} hasn't played any matches yet</Text>
                                    </Box>
                                )}
                                <Text weight={"bold"} style={{ textTransform: "uppercase" }}>
                                    Latest Matches
                                </Text>
                                {pastMatches.map((match) => (
                                    <MatchRow key={match.id} match={match}></MatchRow>
                                ))}
                                {pastMatches.length == 0 && (
                                    <Box align="center">
                                        <Text textAlign="center">{alias.name} has not upcoming matches</Text>
                                    </Box>
                                )}
                            </Box>
                        </Tab>

                        <Tab>
                            <Box gap="small">
                                {tournaments.map((tournament) => (
                                    <TournamentRow key={tournament.id} tournament={tournament}>
                                        <></>
                                    </TournamentRow>
                                ))}
                                {tournaments.length == 0 && (
                                    <Box align="center">
                                        <Text textAlign="center">{alias.name} hasn't played any tournaments yet</Text>
                                    </Box>
                                )}
                            </Box>
                        </Tab>
                        <Tab>
                            <Box gap="small">
                                {leagues.map((league) => (
                                    <Box key={league.id}>
                                        <LeagueRow league={league}>
                                            <></>
                                        </LeagueRow>
                                        <Box pad="small" gap="small">
                                            {league.seasons?.map((season) => (
                                                <SeasonRow key={season.id} season={season}>
                                                    <></>
                                                </SeasonRow>
                                            ))}
                                        </Box>
                                    </Box>
                                ))}
                                {leagues.length == 0 && (
                                    <Box align="center">
                                        <Text textAlign="center">{alias.name} hasn't played any leagues yet</Text>
                                    </Box>
                                )}
                            </Box>
                        </Tab>
                    </Tabs>
                </Box>
            </SkeletonWrapper>
        </Box>
    );
};

export default UXAliasHome;

/* <Tab >
                    <Box gap="medium">
                        <Box background={"background-back"} pad="medium" direction="row" gap="small">
                            <Box justify="center">
                                <img src="/images/rgb.png" width="75px" alt="" />
                            </Box>
                            <Box>
                                <Text>RGB Started Streaming</Text> <LiveIndicator></LiveIndicator>
                                <Text size="small">
                                    Join us on our live streaming on{" "}
                                    <Anchor href="https://www.twitch.tv/directory" target="_blank">
                                        <FontAwesomeIcon icon={["fab", "twitch"]} /> Twitch <FontAwesomeIcon size="sm" icon={["fad", "arrow-up-right-from-square"]} />
                                    </Anchor>
                                </Text>
                            </Box>
                        </Box>

                        <Box background={"background-back"} pad="medium" direction="row" gap="small">
                            <Avatar email={alias.email} name={alias.name} round={"18px"} size="75" src={alias.photoUri}></Avatar>
                            <Box justify="center">
                                <Text size="small">
                                    {alias.name} earned 10 experience for joining a <span style={{ textDecoration: "underline" }}>league match</span>
                                </Text>
                            </Box>
                        </Box>

                        <Box background={"background-back"} pad="medium" direction="row" gap="small">
                            <Box justify="center">
                                <img src="/images/fox.png" width="75px" alt="" />
                            </Box>
                            <Box>
                                <LinkAnchor path={"/teams/1"} label="Fox e-sports Stream"></LinkAnchor>
                                <sup>
                                    <FontAwesomeIcon icon={["fad", "circle-small"]} /> stopped
                                </sup>
                                <Text size="small">
                                    You missed us!
                                    <br />
                                    You can still watch our stream on{" "}
                                    <Anchor href="https://www.twitch.tv/directory" target="_blank">
                                        <FontAwesomeIcon icon={["fab", "twitch"]} /> Twitch <FontAwesomeIcon size="sm" icon={["fad", "arrow-up-right-from-square"]} />
                                    </Anchor>
                                </Text>
                            </Box>
                        </Box>

                        <Box background={"background-back"} pad="medium" direction="row" gap="small">
                            <Box justify="center">
                                <img src="/images/knight.png" width="75px" alt="" />
                            </Box>
                            <Box>
                                <Text>Knight e-sports wins!</Text>
                                <sup>
                                    <Text size="xsmall" color="status-ok">
                                        3
                                    </Text>{" "}
                                    -{" "}
                                    <Text size="xsmall" color="status-critical">
                                        0
                                    </Text>
                                </sup>
                                <Text size="small">
                                    Congratulations to the team!
                                    <br />
                                    <Anchor href="https://www.twitch.tv/directory" target="_blank">
                                        Rate the match <FontAwesomeIcon icon={["fad", "thumbs-up"]} />
                                    </Anchor>
                                </Text>
                            </Box>
                        </Box>
                    </Box>
                </Tab> */
